import type { PosterShapeKind, PosterStickerKind } from "@studio/shared";

/**
 * 도형·스티커 모양 — 편집 화면(SVG)·PNG(canvas Path2D)·PDF(drawSvgPath)가 **같은 경로 문자열**을 쓴다.
 * 좌표계는 0~100 정사각형이고, 그릴 때 요소 상자에 맞춰 늘린다.
 */
export const STICKER_PATHS: Record<PosterStickerKind, string> = {
  star: "M50 6l12.6 27.4 30 3.4-22.4 20.3 6.3 29.5L50 71.6 23.5 86.6l6.3-29.5L7.4 36.8l30-3.4z",
  heart: "M50 88S8 62 8 34a21 21 0 0 1 42-6 21 21 0 0 1 42 6c0 28-42 54-42 54z",
  sparkle: "M50 4c4 26 20 42 46 46-26 4-42 20-46 46-4-26-20-42-46-46 26-4 42-20 46-46z",
  note: "M38 14l46-10v58a13 13 0 1 1-8-12V24L46 30v42a13 13 0 1 1-8-12z",
  burst: "M50 2l9 18 19-9-3 21 21 3-15 15 15 15-21 3 3 21-19-9-9 18-9-18-19 9 3-21-21-3 15-15L4 35l21-3-3-21 19 9z",
  arrow: "M6 42h58V20l30 30-30 30V58H6z",
  bubble: "M14 10h72a10 10 0 0 1 10 10v42a10 10 0 0 1-10 10H44L22 92V72h-8A10 10 0 0 1 4 62V20a10 10 0 0 1 10-10z",
  // 꽃잎 다섯 장(원)을 하나의 경로로 — 가운데는 흰 원을 따로 얹는다.
  flower: "M50 6a18 18 0 1 1 0 36a18 18 0 1 1 0-36zM76 26a18 18 0 1 1 0 36a18 18 0 1 1 0-36zM66 56a18 18 0 1 1 0 36a18 18 0 1 1 0-36zM34 56a18 18 0 1 1 0 36a18 18 0 1 1 0-36zM24 26a18 18 0 1 1 0 36a18 18 0 1 1 0-36z",
};

export const STICKER_NAMES: Record<PosterStickerKind, string> = {
  star: "별", heart: "하트", sparkle: "반짝", note: "음표", burst: "터짐", arrow: "화살표", bubble: "말풍선", flower: "꽃",
};

export const SHAPE_NAMES: Record<PosterShapeKind, string> = {
  rect: "사각형", round: "둥근 사각", circle: "원", triangle: "삼각형", line: "선", pill: "알약",
};

/** 꽃 가운데 흰 원 */
export const FLOWER_CENTER = { cx: 50, cy: 50, r: 13 };

export const POSTER_COLORS = ["#1c1917", "#ffffff", "#17644f", "#e0703a", "#2563eb", "#be185d", "#ca8a04", "#7ec8b5"] as const;

export const POSTER_BACKGROUNDS = [
  "#fbf6ee", "#ffffff", "#f4f0ea", "#1c1917", "#17644f", "#eef4f8", "#fdf2f8", "#fef3c7",
  "linear-gradient(160deg,#fbf6ee,#f3d9c4)", "linear-gradient(180deg,#0f172a,#1e3a8a)",
  "linear-gradient(160deg,#ddf3ec,#fef3c7)", "linear-gradient(180deg,#fdf2f8,#e0e7ff)",
] as const;

/** 배경 그라데이션 «linear-gradient(각도deg,색1,색2)» 를 PNG·PDF 가 그릴 수 있게 푼다. */
export function parseGradient(background: string): { angle: number; from: string; to: string } | null {
  const match = background.match(/^linear-gradient\((-?\d+(?:\.\d+)?)deg,\s*(#[0-9a-f]{6}),\s*(#[0-9a-f]{6})\)$/iu);
  if (!match) return null;
  return { angle: Number(match[1]), from: match[2]!, to: match[3]! };
}

/** 둥근 네모 반지름(mm) — 편집 화면·PNG·PDF 공통 */
export const ROUND_RADIUS_MM = 5;
/** 그림 테두리 두께(mm) */
export const IMAGE_BORDER_MM = 2.5;
/** 도형 테두리 두께(mm) */
export const SHAPE_STROKE_MM = 1.2;
